import { Opts } from "../types";
import "whatwg-fetch"; // polyfill for IE11
import { JSONPatchDocument } from "../types";
import deepmegre from "deepmerge";

const prepareOpts = (internalOpts: RequestInit, usersOpts: Opts = {}) => {
  let opts = { ...internalOpts };

  if (usersOpts.opts) {
    opts = deepmegre(opts, usersOpts.opts);
  }

  if (usersOpts.token) {
    opts = {
      ...opts,
      headers: { ...opts.headers, Authorization: `Bearer ${usersOpts.token}` },
    };
  }

  return opts;
};

const processPotentiallyEmptyResponse = async <T>(response: Response) => {
  const responseText = await response.text();
  const responseObject = responseText?.length ? JSON.parse(responseText) : null;
  return (responseObject as unknown) as T;
};

export abstract class BaseRemoteService {
  constructor(protected apiRoot: string) {}

  protected async getRequest<T>(url: string, opts?: Opts): Promise<T> {
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "GET",
        },
        opts
      ),
      (response) => response.json().then((result) => result as T)
    );
  }

  protected async postRequest<T>(url: string, model: any, opts?: Opts): Promise<T> {
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "POST",
          body: JSON.stringify(model),
          headers: {
            "Content-Type": "application/json",
          },
        },
        opts
      ),
      (response) => processPotentiallyEmptyResponse<T>(response)
    );
  }

  protected async putRequest<T>(url: string, model: any, opts?: Opts): Promise<T> {
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "PUT",
          body: JSON.stringify(model),
          headers: {
            "Content-Type": "application/json",
          },
        },
        opts
      ),
      (response) => processPotentiallyEmptyResponse<T>(response)
    );
  }

  protected async postRequestFormData<T>(url: string, model: any, opts?: Opts): Promise<T> {
    const formData = this.modelToFormData(model);
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "POST",
          body: formData,
        },
        opts
      ),
      (response) => processPotentiallyEmptyResponse<T>(response)
    );
  }

  protected async putRequestFormData<T>(url: string, model: any, opts?: Opts): Promise<T> {
    const formData = this.modelToFormData(model);
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "PUT",
          body: formData,
        },
        opts
      ),
      (response) => processPotentiallyEmptyResponse<T>(response)
    );
  }

  protected async patchRequest<T, U = JSONPatchDocument>(
    url: string,
    body: U,
    opts?: Opts
  ): Promise<T> {
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "PATCH",
          body: JSON.stringify(body),
          headers: {
            "Content-Type": "application/json",
          },
        },
        opts
      ),
      (response) => processPotentiallyEmptyResponse<T>(response)
    );
  }

  protected async patchRequestFormData<T>(url: string, model: any, opts?: Opts): Promise<T> {
    const formData = this.modelToFormData(model);
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "PATCH",
          body: formData,
        },
        opts
      ),
      (response) => processPotentiallyEmptyResponse<T>(response)
    );
  }

  protected async getRequestBlob(url: string, opts?: Opts): Promise<globalThis.Blob> {
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "GET",
          ...opts,
        },
        opts
      ),
      (response) => response.blob()
    );
  }

  protected async deleteRequest(url: string, opts?: Opts): Promise<undefined> {
    return await BaseRemoteService.fetch(
      url,
      prepareOpts(
        {
          method: "DELETE",
          ...opts,
        },
        opts
      ),
      () => Promise.resolve(undefined)
    );
  }

  public static async fetch<T>(
    url: string,
    opts: { [key: string]: any },
    responseBuilder: (response: Response) => Promise<T>
  ): Promise<T> {
    /*
      Happy path:
        0) Eveything is ok
      Expected error conditions:
        1) The API returns a non-success code and a "controlled" JSON object - e.g. validation fail.
        2) The API returns a non-success code and a "uncontrolled" text-based error - 500/server problem
        3) The API returns a success code but body is not JSON so it blows up - seen in development when HOST API env variable is missing
        4) An exception thrown because fetch blows - e.g. no network .
    */

    try {
      const response = await fetch(url, opts);

      if (response.ok) {
        // 0
        return (await responseBuilder(response)) as T;
      } else {
        // 1 , 2
        throw await BaseRemoteService.formatResponseError(response);
      }
    } catch (err) {
      // 3, 4 (and 1, 2 come through here)
      throw BaseRemoteService.formatException(err, { url, opts });
    }
  }

  private static formatException(err: any, args: any) {
    // https://stackoverflow.com/questions/18391212/is-it-not-possible-to-stringify-an-error-using-json-stringify
    // hack to make JavaScript Error exceptions behave as serializable object todo: make this better

    const keys = Object.getOwnPropertyNames(err);

    const errorAsObject = keys.reduce((acc, curr) => {
      acc[curr] = err[curr];
      return acc;
    }, {} as { [key: string]: any });

    return { ...errorAsObject, ...args };
  }

  private static async formatResponseError(
    fetchResponse: Response
  ): Promise<{ [key: string]: any }> {
    const responseText = await fetchResponse.text();
    let body = { responseText };
    try {
      // 1 - if it is a json response, hydrate it and add to body
      const errorObject = JSON.parse(responseText);
      body = { ...body, ...errorObject };
    } catch (e) {
      // 2 - nothing to do, it was never json
    }

    // attach status, statusText, url after body to ensure we get those values from
    //  the response itself (i.e. override any of these values if they are in body)...
    return {
      ...body,
      status: fetchResponse.status,
      statusText: fetchResponse.statusText,
    };
  }

  private modelToFormData(model: { [key: string]: any }): FormData {
    const formData = new FormData();

    for (const key in model) {
      const value = model[key];

      if (value) {
        // if this is a FileList or array of file(s) being uploaded, have to unpack it differently
        if (value.length && Number.isInteger(value.length) && value[0] instanceof File) {
          Array.from(value).forEach((item) => formData.append(key, item as File));
        } else {
          formData.append(key, value);
        }
      }
    }
    return formData;
  }
}
